import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  PhoneOutlined,
  LockOutlined,
  MailOutlined,
  UserAddOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Form, Input, Select, message } from "antd";
import axios from "axios";
import { BASE_URL } from "../../constants/urlConstants";

import {
  selectErrors,
  selectIsAuthLoading,
  selectIsRegistered,
} from "../../redux-toolkit/auth/auth-selector";
import { registration } from "../../redux-toolkit/auth/auth-thunks";
import { resetAuthState, setAuthLoadingState } from "../../redux-toolkit/auth/auth-slice";
import { LoadingStatus, UserRegistration } from "../../types/types";
import FinllieLogo from "../logo/FinllieLogo";
import FinllieLoader from "../logo/FinllieLoader";
import "./Registration.css";

const Registration: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const [form]   = Form.useForm();

  const isRegistered = useSelector(selectIsRegistered);
  const isLoading    = useSelector(selectIsAuthLoading);
  const errors       = useSelector(selectErrors);

  const [distributors,    setDistributors]    = useState<any[]>([]);
  const [loadingDist,     setLoadingDist]     = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setAuthLoadingState(LoadingStatus.LOADED));
    return () => { dispatch(resetAuthState()); };
  }, [dispatch]);

  /* ── Distributor search ── */
  const searchDistributors = async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    const isPincode = /^\d{6}$/.test(trimmed);
    const payload   = isPincode ? { pincode: trimmed } : { distributorId: trimmed };

    setLoadingDist(true);
    setSearchPerformed(true);
    setDistributors([]);

    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.post(
        `${BASE_URL}/api/finalle/search`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { "X-API-Key": token } : {}),
          },
        }
      );

      const data = res.data;
      if (!data || data.length === 0) {
        message.warning("No distributors found. Try a different pincode or ID.");
        return;
      }
      setDistributors(data);
      if (data.length === 1) {
        form.setFieldsValue({ distributorId: data[0].id });
      }
    } catch {
      message.error("Failed to search distributors. Please try again.");
    } finally {
      setLoadingDist(false);
    }
  };

  const onClickSignUp = (userData: UserRegistration): void => {
    dispatch(registration(userData));
  };

  /* ── Success screen ── */
  if (isRegistered) return (
    <div className="reg-page">
      <div className="reg-success">
        <div className="reg-success-icon"><CheckCircleOutlined /></div>
        <h2 className="reg-success-title">Registration Successful!</h2>
        <p className="reg-success-sub">
          Please check your email to activate your account.
        </p>
        <button className="reg-success-btn" onClick={() => history.push("/login")}>
          Go to Login
        </button>
      </div>
    </div>
  );

  return (
    <>
      {isLoading && <FinllieLoader text="Creating your account..." />}

      <div className="reg-page">
        <div className="reg-layout">

          {/* ── LEFT PANEL ── */}
          <div className="reg-left-panel">
            <div className="reg-brand">
              <FinllieLogo wordmark wordmarkWidth={180} animate={false} />
            </div>

            <div className="reg-left-features">
              <div className="reg-feature">
                <span className="reg-feature-icon">💧</span>
                <span>Premium quality water</span>
              </div>
              <div className="reg-feature">
                <span className="reg-feature-icon">🚚</span>
                <span>Same-day delivery</span>
              </div>
              <div className="reg-feature">
                <span className="reg-feature-icon">🔒</span>
                <span>Secure &amp; trusted</span>
              </div>
              <div className="reg-feature">
                <span className="reg-feature-icon">🌿</span>
                <span>With added minerals</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL — form ── */}
          <div className="reg-right-panel">
            <div className="reg-card">
              <div className="reg-card-header">
                <UserAddOutlined className="reg-card-icon" />
                <h2 className="reg-card-title">Create Account</h2>
                <p className="reg-card-sub">
                  Already have an account?{" "}
                  <button className="reg-link-btn" onClick={() => history.push("/login")}>
                    Sign In
                  </button>
                </p>
              </div>

              <Form
                form={form}
                layout="vertical"
                name="registration-form"
                onFinish={onClickSignUp}
                requiredMark={false}
                className="reg-form"
              >
                {/* Row: First + Last name */}
                <div className="reg-form-row">
                  <Form.Item
                    label="First Name" name="firstName"
                    validateStatus={errors.firstNameError ? "error" : ""}
                    help={errors.firstNameError}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="First name" size="large" />
                  </Form.Item>
                  <Form.Item
                    label="Last Name" name="lastName"
                    validateStatus={errors.lastNameError ? "error" : ""}
                    help={errors.lastNameError}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Last name" size="large" />
                  </Form.Item>
                </div>

                {/* Email */}
                <Form.Item
                  label="Email Address" name="email"
                  validateStatus={errors.emailError ? "error" : ""}
                  help={errors.emailError}
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email",  message: "Invalid email address" },
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="you@email.com" size="large" />
                </Form.Item>

                {/* Mobile */}
                <Form.Item
                  label="Mobile Number" name="phoneNumber"
                  rules={[
                    { required: true, message: "Please enter your mobile number" },
                    { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="10-digit mobile number"
                    size="large"
                    maxLength={10}
                    addonBefore="+91"
                  />
                </Form.Item>

                {/* Distributor Search */}
                <div className="reg-dist-search-label">
                  Find Distributor
                  <span className="reg-dist-search-hint">Enter your pincode or Distributor ID</span>
                </div>
                <div className="reg-dist-search-row">
                  <Input
                    prefix={<EnvironmentOutlined />}
                    placeholder="e.g. 560100 or D20263001"
                    size="large"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onPressEnter={() => searchDistributors(searchQuery)}
                    className="reg-dist-search-input"
                  />
                  <button
                    type="button"
                    className="reg-dist-search-btn"
                    onClick={() => searchDistributors(searchQuery)}
                    disabled={loadingDist || !searchQuery.trim()}
                  >
                    {loadingDist ? "..." : <SearchOutlined />}
                  </button>
                </div>

                {/* Distributor dropdown — shown after search */}
                {searchPerformed && distributors.length > 0 && (
                  <Form.Item
                    label="Select Distributor" name="distributorId"
                    rules={[{ required: true, message: "Please select a distributor" }]}
                    style={{ marginTop: 12 }}
                  >
                    <Select
                      placeholder="Choose your distributor"
                      size="large"
                      suffixIcon={<ShopOutlined />}
                    >
                      {distributors.map((dist) => (
                        <Select.Option key={dist.id} value={dist.id}>
                          <div className="reg-dist-option">
                            <span className="reg-dist-name">
                              {dist.businessName}
                              <span className="reg-dist-id"> · {dist.distributorId}</span>
                            </span>
                            <span className="reg-dist-addr">{dist.address} — {dist.pincode}</span>
                          </div>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {searchPerformed && !loadingDist && distributors.length === 0 && (
                  <div className="reg-dist-empty">
                    No distributors found. Try a different pincode or ID.
                  </div>
                )}

                {/* Pincode */}
                <Form.Item
                  label="Your Area Pincode" name="pincode"
                  rules={[{ required: true, message: "Please enter your pincode" }]}
                >
                  <Input
                    prefix={<EnvironmentOutlined />}
                    placeholder="6-digit area pincode"
                    size="large"
                    maxLength={6}
                  />
                </Form.Item>

                {/* Row: Password + Confirm */}
                <div className="reg-form-row">
                  <Form.Item
                    label="Password" name="password"
                    validateStatus={errors.passwordError ? "error" : ""}
                    help={errors.passwordError}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
                  </Form.Item>
                  <Form.Item
                    label="Confirm Password" name="password2"
                    validateStatus={errors.password2Error ? "error" : ""}
                    help={errors.password2Error}
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Input.Password prefix={<LockOutlined />} placeholder="Re-enter password" size="large" />
                  </Form.Item>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="reg-submit-btn"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </button>

                <p className="reg-terms">
                  By signing up you agree to our{" "}
                  <button
                    type="button"
                    className="reg-link-btn"
                    onClick={() => history.push("/terms")}
                  >
                    Terms of Service
                  </button>{" "}
                  and{" "}
                  <button
                    type="button"
                    className="reg-link-btn"
                    onClick={() => history.push("/privacy")}
                  >
                    Privacy Policy
                  </button>
                </p>
              </Form>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Registration;